// StickiesTable.js - Updated with row selection system
import React, { useMemo, useCallback, useState, useEffect } from 'react'; 
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  createColumnHelper
} from '@tanstack/react-table';
import TagDropdown from './TagDropdown';
import { fetchTagDomains } from '../../api/stickiesApi';
import './StickiesListPanelMain.css';
import './TagDropdown.css';

const StickiesTable = ({ 
  stickies, 
  selectedStickies,
  tagDomains = {},
  displayModes,
  onStickyClick,
  onStickySelect,
  onReorderSticky,
  getStickyIcon,
  onTitleUpdate,
  onTagUpdate,
  showContent = true,
  selectedRowId = null, // NEW: Currently selected row for reordering
  onRowSelect = () => {}, // NEW: Row selection handler
}) => {
  const columnHelper = createColumnHelper();
  const [editingTitle, setEditingTitle] = useState(null);
  const [editingValue, setEditingValue] = useState('');

  // Handle domain selection (tag1)
  const handleDomainChange = useCallback((stickyId, domain) => {
    onTagUpdate(stickyId, 0, domain); // tag1 = domain
    onTagUpdate(stickyId, 1, '');     // tag2 = clear
  }, [onTagUpdate]);

  // Handle specific tag selection (tag2)
  const handleTagChange = useCallback((stickyId, tag) => {
    onTagUpdate(stickyId, 1, tag); // tag2 = specific tag
  }, [onTagUpdate]);

  // Get available tags for a specific domain
  const getTagsForDomain = useCallback((domain) => {
    return domain ? tagDomains[domain] || [] : [];
  }, [tagDomains]);

  // Get domain options
  const getDomainOptions = useMemo(() => {
    return Object.keys(tagDomains);
  }, [tagDomains]);

  const columns = useMemo(() => [
    // 1. CHECKBOX FIRST
    columnHelper.accessor('id', {
      id: 'select',
      header: '',
      cell: ({ row }) => (
        <input 
          type="checkbox"
          checked={Boolean(selectedStickies[row.original.id])}
          onChange={(e) => onStickySelect(row.original.id, e.target.checked)}
          onClick={(e) => e.stopPropagation()}
        />
      ),
      size: 25,
    }),
    
    // 2. NUMBER SECOND  
    columnHelper.accessor('list_order', {
      id: 'number',
      header: '#',
      cell: ({ row }) => (
        <div className="sticky-number-column">
          {row.index + 1}
        </div>
      ),
      size: 30,
    }),
    
    // 3. REMOVED REORDER ARROWS - NOW IN HEADER
    
    // 4. TYPE ICON (now 3rd)
    columnHelper.accessor('type', {
      id: 'type-icon',
      header: '',
      cell: ({ row }) => (
        <div className="sticky-type-icon">
          {getStickyIcon(row.original.type)}
        </div>
      ),
      size: 25,
    }),
    
    // 5. VISIBILITY ICON (now 4th)
    columnHelper.accessor('display_mode', {
      id: 'visibility-icon',
      header: '',
      cell: ({ row }) => (
        <div className="sticky-visibility-icon">
          {displayModes[row.original.id] === 'list' ? '🔒' : '👁️'}
        </div>
      ),
      size: 25,
    }),
    
    // 6. TITLE (now 5th) - MORE SPACE!
    columnHelper.accessor('title', {
      id: 'title',
      header: 'Title',
      cell: ({ getValue, row }) => {
        const isEditing = editingTitle === row.original.id;
        
        return isEditing ? (
          <input
            type="text"
            value={editingValue}
            onChange={(e) => setEditingValue(e.target.value)}
            onBlur={() => {
              onTitleUpdate?.(row.original.id, editingValue);
              setEditingTitle(null);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                onTitleUpdate?.(row.original.id, editingValue);
                setEditingTitle(null);
              }
              if (e.key === 'Escape') {
                setEditingTitle(null);
                setEditingValue(getValue() || '');
              }
            }}
            autoFocus
            onClick={(e) => e.stopPropagation()}
            className="table-title-input"
            style={{
              width: '100%',
              border: '1px solid #ccc',
              padding: '2px 4px',
              fontSize: '13px'
            }}
          />
        ) : (
          <div 
            className="sticky-title-main"
            onDoubleClick={(e) => {
              e.stopPropagation();
              setEditingTitle(row.original.id);
              setEditingValue(getValue() || '');
            }}
            title="Double-click to edit title"
            style={{ cursor: 'text' }}
          >
            {getValue() || 'Untitled'}
          </div>
        );
      },
      size: 160, // INCREASED SIZE since no arrows
    }),
    
    // 7. CONTENT (CONDITIONAL) - MORE SPACE!
    ...(showContent ? [
      columnHelper.accessor('content', {
        id: 'content',
        header: 'Content',
        cell: ({ getValue }) => (
          <div className="content-text">
            {getValue()?.substring(0, 200) || 'Empty note...'} {/* More chars */}
          </div>
        ),
        size: 320, // INCREASED SIZE
      })
    ] : []),
    
    // 8. DOMAIN TAG
    columnHelper.accessor('tag1', {
      id: 'tag1',
      header: 'Domain',
      cell: ({ getValue, row }) => {
        return (
          <div onClick={(e) => e.stopPropagation()}>
            <TagDropdown
              value={getValue() || ''}
              onChange={(domain) => handleDomainChange(row.original.id, domain)}
              options={getDomainOptions}
              placeholder="Select domain"
              type="domain"
              disabled={Object.keys(tagDomains).length === 0}
            />
          </div>
        );
      },
      size: 80,
    }),
    
    // 9. SPECIFIC TAG
    columnHelper.accessor('tag2', {
      id: 'tag2', 
      header: 'Tag',
      cell: ({ getValue, row }) => (
        <div onClick={(e) => e.stopPropagation()}>
          <TagDropdown
            value={getValue() || ''}
            onChange={(tag) => handleTagChange(row.original.id, tag)}
            options={getTagsForDomain(row.original.tag1)}
            placeholder="Select tag"
            type="tag"
            disabled={!row.original.tag1 || Object.keys(tagDomains).length === 0}
          />
        </div>
      ),
      size: 80,
    }),
    
    // 10. DATE
    columnHelper.accessor('created_at', {
      id: 'date',
      header: 'Date',
      cell: ({ getValue }) => (
        <div className="sticky-type-main">
          {getValue() ? new Date(getValue()).toLocaleDateString() : 'No date'}
        </div>
      ),
      size: 100,
    }),
  ], [
    selectedStickies, 
    displayModes, 
    onStickySelect, 
    getStickyIcon, 
    editingTitle, 
    editingValue, 
    onTitleUpdate,
    handleDomainChange,
    handleTagChange,
    getDomainOptions,
    getTagsForDomain,
    showContent
  ]);

  // Table instance with stable data reference
  const table = useReactTable({
    data: stickies,
    columns,
    getCoreRowModel: getCoreRowModel(),
    enableRowSelection: true,
    autoResetAll: false,
  });

  return (
    <div className="stickies-list-main">
      <table className="stickies-table">
        <tbody>
          {table.getRowModel().rows.map(row => (
            <tr 
              key={row.original.id}
              className={`sticky-list-item-main ${
                selectedRowId === row.original.id ? 'selected-row' : ''
              }`}
              onClick={(e) => {
                // Check if click is on interactive elements
                const interactiveElements = [
                  'input', 'button', 'select', 
                  '.tag-dropdown-container', 
                  '.table-title-input',
                  '.tag-dropdown-trigger',
                  '.tag-dropdown-menu'
                ];
                
                const isInteractive = interactiveElements.some(selector => 
                  e.target.closest(selector)
                );
                
                if (!isInteractive) {
                  // Check if it's a double-click on title (for editing)
                  if (e.target.closest('.sticky-title-main') && e.detail === 1) {
                    // Single click on title - select row
                    onRowSelect(row.original.id);
                  } else if (!e.target.closest('.sticky-title-main')) {
                    // Click elsewhere - select row OR open sticky
                    if (selectedRowId === row.original.id) {
                      // Already selected - open it
                      onStickyClick(row.original);
                    } else {
                      // Not selected - select it
                      onRowSelect(row.original.id);
                    }
                  }
                }
              }}
            >
              {row.getVisibleCells().map(cell => (
                <td 
                  key={cell.id} 
                  className="sticky-cell"
                  style={{ width: `${cell.column.getSize()}px` }}
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default React.memo(StickiesTable);